import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuX } from "react-icons/lu";
import IdealStudio from "../../../../../core/editor/main";
import { isClmAllowedPlugin } from "../../../../../common/clm";
import { TEMPLATE_SCREEN_TRAIN } from "../../../../../common/constants";
import Alert from "../../../common/alert/Alert";
import { addPluginBox, addHtmlBox } from "../elementActions";
import AssetGrid from "../ui/AssetGrid";
import AssetCell from "../ui/AssetCell";
import BetaTag from "../../../common/BetaTag.jsx";
import ElementPreview from "./ElementPreview";
import RealElementPreview, { hasRealPreview, isScaleRealKind } from "./RealElementPreview";
import "./_panel.scss";
import "./_elementsPanel.scss";

// RichText is intentionally absent: the element was retired (BasicText — now
// displayed as "Rich Text" — covers it). The plugin itself stays registered as
// a BasicText alias so old trainings still load; see plugins/RichText.
const BASIC_ELEMENT_NAMES = [
  "BasicText",
  "HotspotImages",
  "EnrichedPlayer",
  "ShapeOverlay",
  "EnrichedPDF",
];

const PRIMITIVE_NAMES = [
  "BasicText",
  "RichText",
  "HotspotImages",
  "EnrichedPlayer",
  "OneClick",
  "ShapeOverlay",
];

// Image/video drag the real default media (placeholder.svg / taslak.mp4) via
// data-url, so paletteDrag previews it exactly like a dragged library file.
const MEDIA_DRAG = { HotspotImages: "image", EnrichedPlayer: "video" };

// "Other" group items get a Beta tag.
const BETA_NAMES = ["HtmlContent", "AudioCue"];

const BasicElementsPanel = (props) => {
  const {
    boxes,
    boxSelected,
    navItems,
    navItemSelected,
    containedViews,
    containedViewSelected,
    onBoxAdded,
    onClose,
    isClm,
  } = props;

  const [basicButtons, setBasicButtons] = useState([]);
  const [dataButtons, setDataButtons] = useState([]);
  const [otherButtons, setOtherButtons] = useState([]);

  const [showHtmlDialog, setShowHtmlDialog] = useState(false);
  const [useScreenTraining, setUseScreenTraining] = useState(false);
  const [customHtml, setCustomHtml] = useState("");

  useEffect(() => {
    const allConfigs = IdealStudio.Plugins.getPluginConfigs() || [];
    const byName = {};
    allConfigs.forEach((cfg) => {
      byName[cfg.name] = cfg;
    });
    const primitives = BASIC_ELEMENT_NAMES.filter((name) => !isClm || isClmAllowedPlugin(name))
      .map((name) => byName[name])
      .filter(Boolean);
    setBasicButtons(primitives);
    // CLM: data/other groups (tables, charts, PDF, HTML dialog, audio) are not
    // exportable — the allowlisted primitives are the whole palette.
    setDataButtons(
      isClm
        ? []
        : allConfigs.filter((c) => c.category === "text" && !PRIMITIVE_NAMES.includes(c.name))
    );
    setOtherButtons(
      isClm
        ? []
        : allConfigs.filter((c) => c.category === "multimedia" && !PRIMITIVE_NAMES.includes(c.name))
    );
  }, [isClm]);

  const ctx = {
    boxes,
    boxSelected,
    navItems,
    navItemSelected,
    containedViews,
    containedViewSelected,
    onBoxAdded,
  };

  const handlePluginClick = (e, name) => {
    if (name === "HtmlContent") {
      e && e.preventDefault();
      setShowHtmlDialog(true);
      return;
    }
    addPluginBox(name, e, ctx);
  };

  const handleHtmlDialogConfirm = (accepted) => {
    if (accepted) {
      if (useScreenTraining) {
        addHtmlBox("", TEMPLATE_SCREEN_TRAIN, ctx);
      } else if (customHtml && customHtml.trim()) {
        addHtmlBox(customHtml, null, ctx);
      }
    }
    setShowHtmlDialog(false);
    setUseScreenTraining(false);
    setCustomHtml("");
  };

  const renderCard = (cfg) => {
    const label = i18n.t(`PluginNames_${cfg.name}`, { defaultValue: cfg.name });
    // HtmlContent is click-only (needs the dialog to pick content).
    const draggable = cfg.name !== "HtmlContent";
    const media = MEDIA_DRAG[cfg.name];
    const scaleReal = isScaleRealKind(cfg.name);
    let dragProps = {};
    if (draggable) {
      dragProps = {
        className: "palette-item floatingEditorBox",
        name: cfg.name,
        "data-plugin": cfg.name,
      };
      if (media) {
        dragProps["data-url"] = IdealStudio.Plugins.get(cfg.name)?.getInitialState?.()?.url || "";
        dragProps["data-media"] = media;
      } else {
        dragProps["data-schematic"] = "1";
        if (scaleReal) dragProps["data-preview-real-scale"] = "1";
      }
    }
    return (
      <AssetCell
        key={cfg.name}
        dragProps={dragProps}
        ratio={0.82}
        title={label}
        label={label}
        onAdd={(e) => handlePluginClick(e, cfg.name)}
        dragPreview={
          !media && hasRealPreview(cfg.name) ? <RealElementPreview kind={cfg.name} /> : undefined
        }
        badge={BETA_NAMES.includes(cfg.name) ? <BetaTag /> : undefined}
      >
        <ElementPreview kind={cfg.name} icon={cfg.icon} />
      </AssetCell>
    );
  };

  return (
    <>
      <div className="cmp-panel">
        <div className="cmp-panel__toolbar">
          <span className="cmp-panel__title">{i18n.t("basic_elements") || "Öğeler"}</span>
          {onClose && (
            <button
              type="button"
              className="cmp-panel__close"
              onClick={onClose}
              title={i18n.t("close")}
            >
              <LuX size={15} />
            </button>
          )}
        </div>

        <div className="cmp-panel__scroll">
          <div className="cmp-el-list">
            <div className="cmp-el-group">
              <AssetGrid columns={2} cellSize={118} gap={10}>
                {basicButtons.map(renderCard)}
              </AssetGrid>
            </div>

            {dataButtons.length > 0 && (
              <div className="cmp-el-group">
                <div className="cmp-el-group__title">{i18n.t("data") || "Veri"}</div>
                <AssetGrid columns={2} cellSize={118} gap={10}>
                  {dataButtons.map(renderCard)}
                </AssetGrid>
              </div>
            )}

            {otherButtons.length > 0 && (
              <div className="cmp-el-group">
                <div className="cmp-el-group__title">{i18n.t("other") || "Other"}</div>
                <AssetGrid columns={2} cellSize={118} gap={10}>
                  {otherButtons.map(renderCard)}
                </AssetGrid>
              </div>
            )}
          </div>
        </div>
      </div>

      <Alert
        show={showHtmlDialog}
        hasHeader
        closeButton
        title={
          <span>
            <i className="material-icons" style={{ verticalAlign: "middle", marginRight: 8 }}>
              code
            </i>
            {i18n.t("HtmlContentAdd") || "HTML İçerik Ekle"}
          </span>
        }
        cancelButton
        cancelButtonText={i18n.t("messages.Cancel")}
        acceptButtonText={i18n.t("messages.OK")}
        onClose={handleHtmlDialogConfirm}
        className="pageModal"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "8px 0" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              padding: "12px 16px",
              background: useScreenTraining ? "#F0EBFF" : "#F8F9FA",
              border: useScreenTraining ? "2px solid #7F56D9" : "1px solid #E2E8F0",
              borderRadius: 8,
              transition: "all 0.2s ease",
            }}
          >
            <input
              type="checkbox"
              checked={useScreenTraining}
              onChange={(e) => {
                setUseScreenTraining(e.target.checked);
                if (e.target.checked) setCustomHtml("");
              }}
              style={{ width: 18, height: 18, accentColor: "#7F56D9" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontWeight: 600, fontSize: "14px", color: "#1E293B" }}>
                {i18n.t("ScreenTraining") || "Ekran Eğitimi"}
              </span>
              <span style={{ fontSize: "12px", color: "#64748B" }}>
                {i18n.t("ScreenTrainingUseReadyTemplate") || "Hazır ekran eğitimi şablonunu kullan"}
              </span>
            </div>
          </label>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontWeight: 600, fontSize: "13px", color: "#374151" }}>
              {i18n.t("CustomHtmlContent") || "Özel HTML İçerik"}
            </label>
            <textarea
              value={customHtml}
              onChange={(e) => {
                setCustomHtml(e.target.value);
                if (e.target.value.trim()) setUseScreenTraining(false);
              }}
              disabled={useScreenTraining}
              placeholder={
                i18n.t("PasteHtmlContentHere") || "<div>HTML içeriğinizi buraya yapıştırın...</div>"
              }
              style={{
                width: "100%",
                minHeight: 150,
                padding: "12px",
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                fontFamily: "'Courier New', Courier, monospace",
                fontSize: "13px",
                resize: "vertical",
                background: useScreenTraining ? "#F1F5F9" : "#FFFFFF",
                color: useScreenTraining ? "#94A3B8" : "#1E293B",
                outline: "none",
              }}
            />
          </div>
        </div>
      </Alert>
    </>
  );
};

BasicElementsPanel.propTypes = {
  boxes: PropTypes.object.isRequired,
  boxSelected: PropTypes.any,
  navItems: PropTypes.object.isRequired,
  navItemSelected: PropTypes.any.isRequired,
  containedViews: PropTypes.object.isRequired,
  containedViewSelected: PropTypes.any.isRequired,
  onBoxAdded: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  isClm: PropTypes.bool,
};

export default BasicElementsPanel;
